/**
 * Campaign Launch Modal state — manages the full publish-to-Meta flow.
 */
import { create } from "zustand";
import type {
  MetaPage,
  MetaPixel,
  MetaTargetingResult,
  PublishCampaignResult,
} from "@/features/ads/services/metaAdsApi";

// ── Constants ────────────────────────────────────────────────────────────────

export const META_OBJECTIVES = [
  { value: "OUTCOME_AWARENESS", label: "Awareness" },
  { value: "OUTCOME_TRAFFIC", label: "Traffic" },
  { value: "OUTCOME_ENGAGEMENT", label: "Engagement" },
  { value: "OUTCOME_LEADS", label: "Leads" },
  { value: "OUTCOME_APP_PROMOTION", label: "App promotion" },
  { value: "OUTCOME_SALES", label: "Sales" },
] as const;

export const SPECIAL_AD_CATEGORIES = [
  { value: "HOUSING", label: "Housing" },
  { value: "EMPLOYMENT", label: "Employment" },
  { value: "FINANCIAL_PRODUCTS_SERVICES", label: "Financial products & services" },
  { value: "ISSUES_ELECTIONS_POLITICS", label: "Issues, elections or politics" },
  { value: "ONLINE_GAMBLING_AND_GAMING", label: "Online gambling & gaming" },
] as const;

export const CONVERSION_LOCATIONS = [
  { value: "WEBSITE", label: "Website" },
  { value: "APP", label: "App" },
  { value: "MESSENGER", label: "Messenger" },
  { value: "INSTAGRAM_DIRECT", label: "Instagram Direct" },
] as const;

export const PERFORMANCE_GOALS = [
  { value: "LINK_CLICKS", label: "Link Clicks" },
  { value: "LANDING_PAGE_VIEWS", label: "Landing Page Views" },
  { value: "OFFSITE_CONVERSIONS", label: "Conversions" },
  { value: "REACH", label: "Reach" },
  { value: "IMPRESSIONS", label: "Impressions" },
  { value: "VALUE", label: "Value" },
  { value: "LEAD_GENERATION", label: "Lead Generation" },
] as const;

export const CTA_TYPES = [
  { value: "LEARN_MORE", label: "Learn More" },
  { value: "SHOP_NOW", label: "Shop Now" },
  { value: "SIGN_UP", label: "Sign Up" },
  { value: "BOOK_NOW", label: "Book Now" },
  { value: "CONTACT_US", label: "Contact Us" },
  { value: "DOWNLOAD", label: "Download" },
  { value: "GET_QUOTE", label: "Get Quote" },
  { value: "APPLY_NOW", label: "Apply Now" },
  { value: "SUBSCRIBE", label: "Subscribe" },
  { value: "ORDER_NOW", label: "Order Now" },
  { value: "BUY_NOW", label: "Buy Now" },
  { value: "GET_OFFER", label: "Get Offer" },
] as const;

export const CONVERSION_EVENTS = [
  { value: "PURCHASE", label: "Purchase" },
  { value: "ADD_TO_CART", label: "Add to Cart" },
  { value: "COMPLETE_REGISTRATION", label: "Complete Registration" },
  { value: "LEAD", label: "Lead" },
  { value: "VIEW_CONTENT", label: "View Content" },
  { value: "INITIATE_CHECKOUT", label: "Initiate Checkout" },
  { value: "SEARCH", label: "Search" },
  { value: "ADD_PAYMENT_INFO", label: "Add Payment Info" },
] as const;

// ── State types ──────────────────────────────────────────────────────────────

export type LaunchState = "idle" | "validating" | "publishing" | "done" | "error";

export interface CampaignConfig {
  campaignName: string;
  objective: string;
  specialAdCategories: string[];
  selectedPageId: string;
  selectedInstagramUserId: string;
}

export interface ConversionConfig {
  adSetName: string;
  conversionLocation: string;
  performanceGoal: string;
  pixelId: string;
  conversionEvent: string;
}

export interface AudienceConfig {
  advantagePlusEnabled: boolean;
  locations: MetaTargetingResult[];
  ageMin: number;
  ageMax: number;
  gender: "all" | "male" | "female";
}

export interface BudgetConfig {
  allocationLevel: "campaign" | "adset";
  dailyBudget: string;
  currency: string;
  startDate: string;
  endDate: string;
  hasEndDate: boolean;
}

export interface CreativeConfig {
  assetUrls: string[];
  primaryText: string;
  headline: string;
  ctaType: string;
  destinationUrl: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
}

interface CampaignLaunchState {
  // Modal visibility
  isOpen: boolean;
  preselectedAssetUrl: string | null;
  preselectedAssetType: "image" | "video" | null;

  // Section configs
  campaign: CampaignConfig;
  conversion: ConversionConfig;
  audience: AudienceConfig;
  budget: BudgetConfig;
  creative: CreativeConfig;

  // Fetched data
  pages: MetaPage[];
  pixels: MetaPixel[];
  pagesLoading: boolean;
  pixelsLoading: boolean;

  // Launch state
  launchState: LaunchState;
  launchError: string | null;
  publishResult: PublishCampaignResult | null;
  validationErrors: Record<string, string>;

  // Preview
  previewHtml: string | null;
  previewTab: "edit" | "preview";

  // Actions
  openModal: (assetUrl: string, assetType: "image" | "video") => void;
  closeModal: () => void;
  setCampaign: (updates: Partial<CampaignConfig>) => void;
  setConversion: (updates: Partial<ConversionConfig>) => void;
  setAudience: (updates: Partial<AudienceConfig>) => void;
  setBudget: (updates: Partial<BudgetConfig>) => void;
  setCreative: (updates: Partial<CreativeConfig>) => void;
  setPages: (pages: MetaPage[]) => void;
  setPixels: (pixels: MetaPixel[]) => void;
  setPagesLoading: (loading: boolean) => void;
  setPixelsLoading: (loading: boolean) => void;
  setLaunchState: (state: LaunchState) => void;
  setLaunchError: (error: string | null) => void;
  setPublishResult: (result: PublishCampaignResult | null) => void;
  setValidationErrors: (errors: Record<string, string>) => void;
  setPreviewHtml: (html: string | null) => void;
  setPreviewTab: (tab: "edit" | "preview") => void;
  reset: () => void;
}

const today = new Date().toISOString().split("T")[0];

const defaultCampaign: CampaignConfig = {
  campaignName: "",
  objective: "OUTCOME_TRAFFIC",
  specialAdCategories: [],
  selectedPageId: "",
  selectedInstagramUserId: "",
};

const defaultConversion: ConversionConfig = {
  adSetName: "",
  conversionLocation: "WEBSITE",
  performanceGoal: "LINK_CLICKS",
  pixelId: "",
  conversionEvent: "",
};

const defaultAudience: AudienceConfig = {
  advantagePlusEnabled: true,
  locations: [],
  ageMin: 18,
  ageMax: 65,
  gender: "all",
};

const defaultBudget: BudgetConfig = {
  allocationLevel: "campaign",
  dailyBudget: "",
  currency: "AED",
  startDate: today,
  endDate: "",
  hasEndDate: false,
};

const defaultCreative: CreativeConfig = {
  assetUrls: [],
  primaryText: "",
  headline: "",
  ctaType: "LEARN_MORE",
  destinationUrl: "",
  utmSource: "",
  utmMedium: "",
  utmCampaign: "",
  utmContent: "",
};

export const useCampaignLaunchStore = create<CampaignLaunchState>((set) => ({
  isOpen: false,
  preselectedAssetUrl: null,
  preselectedAssetType: null,

  campaign: { ...defaultCampaign },
  conversion: { ...defaultConversion },
  audience: { ...defaultAudience },
  budget: { ...defaultBudget },
  creative: { ...defaultCreative },

  pages: [],
  pixels: [],
  pagesLoading: false,
  pixelsLoading: false,

  launchState: "idle",
  launchError: null,
  publishResult: null,
  validationErrors: {},

  previewHtml: null,
  previewTab: "edit",

  openModal: (assetUrl, assetType) => {
    const now = new Date();
    const datePart = now.toISOString().split("T")[0].replace(/-/g, "-");
    const timePart = now.toTimeString().slice(0, 5).replace(":", "");
    const autoName = `Account Admin's Campaign - ${datePart} ${timePart}`;
    const autoAdSetName = `Account Admin's Adset - ${datePart} ${timePart}`;
    set({
      isOpen: true,
      preselectedAssetUrl: assetUrl,
      preselectedAssetType: assetType,
      campaign: { ...defaultCampaign, campaignName: autoName },
      conversion: { ...defaultConversion, adSetName: autoAdSetName },
      audience: { ...defaultAudience },
      budget: { ...defaultBudget, startDate: now.toISOString().split("T")[0] },
      creative: { ...defaultCreative, assetUrls: [assetUrl] },
      launchState: "idle",
      launchError: null,
      publishResult: null,
      validationErrors: {},
      previewHtml: null,
      previewTab: "edit",
    });
  },
  closeModal: () => set({ isOpen: false }),
  setCampaign: (updates) => set((s) => ({ campaign: { ...s.campaign, ...updates } })),
  setConversion: (updates) => set((s) => ({ conversion: { ...s.conversion, ...updates } })),
  setAudience: (updates) => set((s) => ({ audience: { ...s.audience, ...updates } })),
  setBudget: (updates) => set((s) => ({ budget: { ...s.budget, ...updates } })),
  setCreative: (updates) => set((s) => ({ creative: { ...s.creative, ...updates } })),
  setPages: (pages) => set({ pages }),
  setPixels: (pixels) => set({ pixels }),
  setPagesLoading: (pagesLoading) => set({ pagesLoading }),
  setPixelsLoading: (pixelsLoading) => set({ pixelsLoading }),
  setLaunchState: (launchState) => set({ launchState }),
  setLaunchError: (launchError) => set({ launchError }),
  setPublishResult: (publishResult) => set({ publishResult }),
  setValidationErrors: (validationErrors) => set({ validationErrors }),
  setPreviewHtml: (previewHtml) => set({ previewHtml }),
  setPreviewTab: (previewTab) => set({ previewTab }),
  reset: () =>
    set({
      isOpen: false,
      preselectedAssetUrl: null,
      preselectedAssetType: null,
      campaign: { ...defaultCampaign },
      conversion: { ...defaultConversion },
      audience: { ...defaultAudience },
      budget: { ...defaultBudget },
      creative: { ...defaultCreative },
      pages: [],
      pixels: [],
      pagesLoading: false,
      pixelsLoading: false,
      launchState: "idle",
      launchError: null,
      publishResult: null,
      validationErrors: {},
      previewHtml: null,
      previewTab: "edit",
    }),
}));
